import { useApolloClient, useQuery, useSuspenseQuery } from "@apollo/client";
import { ThemeProvider } from "@openuidev/react-ui/ThemeProvider";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "@repo/ui";
import {
  type CashManagementPlaygroundCashflowEdits,
  type CashManagementPlaygroundInputs,
  cashManagementPlaygroundStreamEventSchema,
  cashManagementPlaygroundThreadSchema,
  neverFunc,
} from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { typeid } from "typeid-js";
import { z } from "zod";
import { PlaygroundMessage } from "~/components/cash-management-playground/message";
import { OrganisationSelector } from "~/components/cash-management-playground/organisation-selector";
import { restoreThread } from "~/components/cash-management-playground/thread";
import type {
  ActivityStep,
  ChatTurn,
} from "~/components/cash-management-playground/types";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";
import "@openuidev/react-ui/components.css";
import "@openuidev/react-ui/defaults.css";

export const Route = createFileRoute("/__auth/cash-management-playground")({
  head: () => opsHead("Cash management playground"),
  validateSearch: z.object({
    threadId: z
      .string()
      .startsWith("cash_mgt_strategy_thread_")
      .optional()
      .catch(undefined),
  }),
  component: CashManagementPlayground,
});

type GenerationRequest = {
  threadId: string;
  attemptId: string;
  parentRunId?: string;
  messages: { role: "user" | "assistant"; content: string }[];
  organisationId?: string;
  holdingEntityId?: string;
  inputSource?: "CHAT" | "FORM";
  cashflowConfirmation?: string;
  cashflowEdits?: CashManagementPlaygroundCashflowEdits;
  planningInputs?: CashManagementPlaygroundInputs;
  assistantId: string;
  userMessage?: string;
};

const cashManagementStrategySubscription = graphql(`
  subscription CashManagementStrategy($input: JSON!) {
    cashManagementStrategy(input: $input) {
      event
    }
  }
`);

const playgroundOrganisationsQuery = graphql(`
  query CashManagementPlaygroundOrganisations {
    organisations {
      id
      name
      squareLogoUrl
    }
  }
`);

const cashManagementStrategyThreadQuery = graphql(`
  query CashManagementStrategyThread($id: ID!) {
    cashManagementStrategyThread(id: $id) {
      id
      organisationId
      createdAt
      updatedAt
      content
    }
  }
`);

const cancelCashManagementStrategyMutation = graphql(`
  mutation CancelCashManagementStrategy($threadId: ID!, $attemptId: ID!) {
    cancelCashManagementStrategy(threadId: $threadId, attemptId: $attemptId) {
      cancelled
    }
  }
`);

function CashManagementPlayground() {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { threadId: urlThreadId } = Route.useSearch();
  const threadId = useRef<string | undefined>(urlThreadId);
  const locallyStartedThreadId = useRef<string | undefined>(undefined);
  const [hydratedThreadId, setHydratedThreadId] = useState<string>();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [organisationId, setOrganisationId] = useState<string>();
  const { data: organisationData } = useSuspenseQuery(
    playgroundOrganisationsQuery,
  );
  const [holdingEntityId, setHoldingEntityId] = useState<string>();
  const [cashflowEdits, setCashflowEdits] =
    useState<CashManagementPlaygroundCashflowEdits>();
  const [cashflowConfirmation, setCashflowConfirmation] = useState<string>();
  const [planningInputs, setPlanningInputs] =
    useState<CashManagementPlaygroundInputs>({});
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const skipThreadQuery =
    !urlThreadId || urlThreadId === locallyStartedThreadId.current || busy;
  const {
    data: threadData,
    loading: threadLoading,
    error: threadError,
  } = useQuery(cashManagementStrategyThreadQuery, {
    variables: { id: urlThreadId ?? "" },
    skip: skipThreadQuery,
    fetchPolicy: "no-cache",
  });
  const loadingThread =
    !skipThreadQuery && (threadLoading || hydratedThreadId !== urlThreadId);
  const [notice, setNotice] = useState<string>();
  const [showJump, setShowJump] = useState(false);
  const active = useRef<AbortController | null>(null);
  const pending = useRef<GenerationRequest | null>(null);
  const viewport = useRef<HTMLDivElement | null>(null);
  const threadContent = useRef<HTMLDivElement | null>(null);
  const follow = useRef(true);
  const composer = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (skipThreadQuery || !urlThreadId || hydratedThreadId === urlThreadId) {
      return;
    }
    if (threadLoading) {
      return;
    }
    if (threadError) {
      setNotice(threadError.message);
      setHydratedThreadId(urlThreadId);
      return;
    }
    if (
      threadData?.cashManagementStrategyThread &&
      threadData.cashManagementStrategyThread.id !== urlThreadId
    ) {
      return;
    }
    try {
      const parsed = cashManagementPlaygroundThreadSchema.parse(
        threadData?.cashManagementStrategyThread?.content,
      );
      const restored = restoreThread(parsed);
      const latest = parsed.runs.at(-1)?.request;
      threadId.current = urlThreadId;
      setTurns(restored);
      setOrganisationId(latest?.organisationId);
      setHoldingEntityId(
        latest?.holdingEntityId ??
          restored.findLast((turn) => turn.holdingEntityId)?.holdingEntityId,
      );
      setCashflowConfirmation(latest?.cashflowConfirmation);
      setCashflowEdits(latest?.cashflowEdits);
      setPlanningInputs(latest?.planningInputs ?? {});
      const lastRun = parsed.runs.at(-1);
      if (lastRun && lastRun.attempts.at(-1)?.status !== "complete") {
        pending.current = {
          ...lastRun.request,
          assistantId: lastRun.id,
        };
      }
      setHydratedThreadId(urlThreadId);
    } catch (error) {
      setNotice(
        error instanceof Error ? error.message : "Unable to load thread.",
      );
      setHydratedThreadId(urlThreadId);
    }
  }, [
    hydratedThreadId,
    skipThreadQuery,
    threadData,
    threadError,
    threadLoading,
    urlThreadId,
  ]);

  const generate = useCallback(
    async (request: GenerationRequest): Promise<void> => {
      if (active.current) {
        return;
      }
      const controller = new AbortController();
      active.current = controller;
      threadId.current = request.threadId;
      locallyStartedThreadId.current = request.threadId;
      void navigate({
        to: "/cash-management-playground",
        search: { threadId: request.threadId },
        replace: true,
      });
      pending.current = request;
      follow.current = true;
      const startedAt = Date.now();
      setBusy(true);
      setNotice(undefined);
      if (request.organisationId) {
        setOrganisationId(request.organisationId);
      }
      if (request.holdingEntityId) {
        setHoldingEntityId(request.holdingEntityId);
      }
      if (request.cashflowConfirmation) {
        setCashflowConfirmation(request.cashflowConfirmation);
      }
      setCashflowEdits(request.cashflowEdits);
      setPlanningInputs(request.planningInputs ?? {});
      const assistant: ChatTurn = {
        id: request.assistantId,
        organisationId: request.organisationId,
        holdingEntityId: request.holdingEntityId,
        strategyReady: request.planningInputs?.additionalContext !== undefined,
        fundingConfirmationReady:
          Boolean(request.cashflowConfirmation) &&
          !request.planningInputs?.funding,
        additionalConstraintsReady:
          Boolean(request.planningInputs?.preferences) &&
          request.planningInputs?.additionalContext === undefined,
        role: "assistant",
        content: "",
        activities: [],
        status: "streaming",
      };
      setTurns((previous) => {
        if (previous.some((turn) => turn.id === request.assistantId)) {
          return previous.map((turn) =>
            turn.id === request.assistantId ? assistant : turn,
          );
        }
        const added: ChatTurn[] = [];
        if (request.userMessage) {
          added.push({
            id: typeid("cash_mgt_strategy_message").toString(),
            role: "user",
            content: request.userMessage,
            activities: [],
            status: "complete",
          });
        }
        return [...previous, ...added, assistant];
      });
      let content = "";
      let activities: ActivityStep[] = [];
      try {
        await new Promise<void>((resolve, reject) => {
          let completed = false;
          let settled = false;
          let subscription: { unsubscribe: () => void } | undefined;
          const finish = (error?: Error): void => {
            if (settled) {
              return;
            }
            settled = true;
            controller.signal.removeEventListener("abort", onAbort);
            subscription?.unsubscribe();
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          };
          const onAbort = (): void => finish();
          controller.signal.addEventListener("abort", onAbort, { once: true });
          if (controller.signal.aborted) {
            onAbort();
            return;
          }
          subscription = apolloClient
            .subscribe({
              query: cashManagementStrategySubscription,
              variables: {
                input: {
                  messages: request.messages,
                  threadId: request.threadId,
                  runId: request.assistantId,
                  attemptId: request.attemptId,
                  parentRunId: request.parentRunId,
                  organisationId: request.organisationId,
                  holdingEntityId: request.holdingEntityId,
                  inputSource: request.inputSource,
                  cashflowConfirmation: request.cashflowConfirmation,
                  cashflowEdits: request.cashflowEdits,
                  planningInputs: request.planningInputs,
                },
              },
              fetchPolicy: "no-cache",
            })
            .subscribe({
              next: ({ data, errors }) => {
                if (
                  controller.signal.aborted ||
                  active.current !== controller
                ) {
                  return;
                }
                if (errors?.length) {
                  finish(new Error(errors[0].message));
                  return;
                }
                try {
                  const event = cashManagementPlaygroundStreamEventSchema.parse(
                    data?.cashManagementStrategy?.event,
                  );
                  if ("id" in event) {
                    const previous = activities.find(
                      (step) => step.id === event.id,
                    );
                    let step: ActivityStep;
                    switch (event.type) {
                      case "reasoning-start":
                        step = {
                          id: event.id,
                          kind: "thinking",
                          name: "Thinking summary",
                          text: "",
                          status: "running",
                        };
                        break;
                      case "reasoning-delta":
                        step = {
                          id: event.id,
                          kind: "thinking",
                          name: "Thinking summary",
                          text: (previous?.text ?? "") + event.text,
                          status: "running",
                        };
                        break;
                      case "reasoning-end":
                        step = {
                          id: event.id,
                          kind: "thinking",
                          name: "Thinking summary",
                          text: previous?.text ?? "",
                          status: "complete",
                        };
                        break;
                      case "tool-start":
                        step = {
                          id: event.id,
                          kind: "tool",
                          name: event.name,
                          text: "",
                          status: "running",
                        };
                        break;
                      case "tool-input":
                        step = {
                          id: event.id,
                          kind: "tool",
                          name: previous?.name ?? "Tool",
                          text: (previous?.text ?? "") + event.delta,
                          status: "running",
                        };
                        break;
                      case "tool-call":
                        step = {
                          id: event.id,
                          kind: "tool",
                          name: event.name,
                          text: JSON.stringify(event.input, null, 2),
                          input: event.input,
                          status: "running",
                        };
                        break;
                      case "tool-result":
                        step = {
                          id: event.id,
                          kind: "tool",
                          name: event.name,
                          text: previous?.text ?? "",
                          input: previous?.input,
                          output: event.output,
                          status: "complete",
                        };
                        break;
                      case "tool-error":
                        step = {
                          id: event.id,
                          kind: "tool",
                          name: event.name,
                          text: previous?.text ?? "",
                          input: previous?.input,
                          output: event.message,
                          status: "error",
                        };
                        break;
                      default:
                        return neverFunc(event);
                    }
                    activities = previous
                      ? activities.map((item) =>
                          item.id === event.id ? step : item,
                        )
                      : [...activities, step];
                  } else {
                    switch (event.type) {
                      case "current-balances":
                        setTurns((previous) =>
                          previous.map((turn) =>
                            turn.id === request.assistantId
                              ? { ...turn, fundingReview: event }
                              : turn,
                          ),
                        );
                        break;
                      case "product-preferences":
                        setTurns((previous) =>
                          previous.map((turn) =>
                            turn.id === request.assistantId
                              ? { ...turn, productReview: event }
                              : turn,
                          ),
                        );
                        break;
                      case "edit-step":
                        setTurns((previous) =>
                          previous.map((turn) =>
                            turn.id === request.assistantId
                              ? {
                                  ...turn,
                                  editStep: event.step,
                                  fundingConfirmationReady:
                                    event.step === "FUNDING",
                                  additionalConstraintsReady:
                                    event.step === "CONTEXT",
                                }
                              : turn,
                          ),
                        );
                        break;
                      case "cashflow-report":
                        setTurns((previous) =>
                          previous.map((turn) =>
                            turn.id === request.assistantId
                              ? { ...turn, cashflowReport: event }
                              : turn,
                          ),
                        );
                        break;
                      case "selection":
                        setTurns((previous) =>
                          previous.map((turn) =>
                            turn.id === request.assistantId
                              ? { ...turn, entities: event.entities }
                              : turn,
                          ),
                        );
                        if (event.holdingEntityId) {
                          setHoldingEntityId(event.holdingEntityId);
                          setTurns((previous) =>
                            previous.map((turn) =>
                              turn.id === request.assistantId
                                ? {
                                    ...turn,
                                    holdingEntityId: event.holdingEntityId,
                                  }
                                : turn,
                            ),
                          );
                        }
                        break;
                      case "text-delta":
                        content += event.text;
                        break;
                      case "completion":
                        setTurns((previous) =>
                          previous.map((turn) =>
                            turn.id === request.assistantId
                              ? {
                                  ...turn,
                                  content,
                                  activities,
                                  status: "complete",
                                  elapsedMs: Date.now() - startedAt,
                                }
                              : turn,
                          ),
                        );
                        pending.current = null;
                        break;
                      case "error":
                        throw new Error(event.message);
                      default:
                        return neverFunc(event);
                    }
                  }
                  if (event.type !== "completion") {
                    setTurns((previous) =>
                      previous.map((turn) =>
                        turn.id === request.assistantId
                          ? { ...turn, content, activities }
                          : turn,
                      ),
                    );
                  }
                  if (event.type === "completion") {
                    completed = true;
                    finish();
                  }
                } catch (cause) {
                  finish(
                    cause instanceof Error
                      ? cause
                      : new Error("Generation failed. Please retry."),
                  );
                }
              },
              error: (cause: unknown) =>
                finish(
                  cause instanceof Error
                    ? cause
                    : new Error("Generation failed. Please retry."),
                ),
              complete: () =>
                finish(
                  completed || controller.signal.aborted
                    ? undefined
                    : new Error(
                        "The stream ended before completion. Please retry.",
                      ),
                ),
            });
          if (settled) {
            subscription.unsubscribe();
          }
        });
      } catch (cause) {
        if (!controller.signal.aborted && active.current === controller) {
          const message =
            cause instanceof Error
              ? cause.message
              : "Generation failed. Please retry.";
          setTurns((previous) =>
            previous.map((turn) =>
              turn.id === request.assistantId
                ? {
                    ...turn,
                    status: "error",
                    error: message,
                    elapsedMs: Date.now() - startedAt,
                    activities: turn.activities.map((step) =>
                      step.status === "running"
                        ? { ...step, status: "cancelled" }
                        : step,
                    ),
                  }
                : turn,
            ),
          );
        }
      } finally {
        if (active.current === controller) {
          active.current = null;
          setBusy(false);
          composer.current?.focus();
        }
      }
    },
    [apolloClient, navigate],
  );

  useEffect(() => {
    return () => {
      active.current?.abort();
      active.current = null;
    };
  }, []);

  useEffect(() => {
    const element = viewport.current;
    const content = threadContent.current;
    if (!element || !content) {
      return;
    }
    const observer = new ResizeObserver(() => {
      if (follow.current) {
        element.scrollTo({ top: element.scrollHeight, behavior: "instant" });
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const reportRenderError = useCallback((id: string): void => {
    setTurns((previous) =>
      previous.map((turn) =>
        turn.id === id
          ? {
              ...turn,
              error:
                "This reply could not be displayed. Try another message or start a new chat.",
            }
          : turn,
      ),
    );
  }, []);

  const send = (
    content: string,
    selectedOrganisationId = organisationId,
    entityId = holdingEntityId,
    confirmation = cashflowConfirmation,
    inputs = planningInputs,
    edits = cashflowEdits,
    inputSource: "CHAT" | "FORM" = "CHAT",
  ): void => {
    if (active.current || !selectedOrganisationId || !content.trim()) {
      return;
    }
    const messages = turns
      .filter((turn) => turn.role === "user" || turn.status === "complete")
      .map(({ role, content }) => ({ role, content }));
    void generate({
      threadId:
        threadId.current ?? typeid("cash_mgt_strategy_thread").toString(),
      attemptId: typeid("cash_mgt_strategy_attempt").toString(),
      parentRunId: turns.findLast((turn) => turn.role === "assistant")?.id,
      organisationId: selectedOrganisationId,
      holdingEntityId: entityId,
      inputSource,
      cashflowConfirmation: confirmation,
      cashflowEdits: edits,
      planningInputs: inputs,
      messages: [...messages, { role: "user", content: content.trim() }],
      userMessage: content.trim(),
      assistantId: typeid("cash_mgt_strategy_run").toString(),
    });
    setDraft("");
  };

  return (
    <ThemeProvider cssSelector="#cash-management-playground">
      <main
        id="cash-management-playground"
        className="flex h-[calc(100dvh-2rem)] min-h-0 shrink-0 flex-col overflow-hidden"
      >
        {threadId.current && !loadingThread && (
          <div className="flex justify-end border-b px-4 py-2 sm:px-8">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                threadId.current = undefined;
                locallyStartedThreadId.current = undefined;
                setHydratedThreadId(undefined);
                pending.current = null;
                setTurns([]);
                setOrganisationId(undefined);
                setHoldingEntityId(undefined);
                setCashflowConfirmation(undefined);
                setCashflowEdits(undefined);
                setPlanningInputs({});
                setNotice(undefined);
                void navigate({
                  to: "/cash-management-playground",
                  search: {},
                  replace: true,
                });
              }}
            >
              New chat
            </Button>
          </div>
        )}
        <Dialog open={!organisationId && !loadingThread}>
          <DialogContent requireIntentionalClosing hideCloseButton>
            <DialogHeader>
              <DialogTitle>Choose an organisation</DialogTitle>
              <DialogDescription>
                Select an organisation to begin its cashflow review and chat.
              </DialogDescription>
            </DialogHeader>
            {organisationData?.organisations?.length ? (
              <OrganisationSelector
                organisations={organisationData.organisations.map((org) => ({
                  id: org.id,
                  name: org.name,
                  logoUrl: org.squareLogoUrl ?? null,
                }))}
                enabled
                onSelect={(selectedOrganisationId) => {
                  const content =
                    "Review this organisation’s three-month average cashflow and help me select its holding entity.";
                  setOrganisationId(selectedOrganisationId);
                  void generate({
                    threadId:
                      threadId.current ??
                      typeid("cash_mgt_strategy_thread").toString(),
                    attemptId: typeid("cash_mgt_strategy_attempt").toString(),
                    messages: [{ role: "user", content }],
                    organisationId: selectedOrganisationId,
                    inputSource: "FORM",
                    userMessage: content,
                    assistantId: typeid("cash_mgt_strategy_run").toString(),
                  });
                }}
              />
            ) : (
              <p className="text-secondary text-sm">
                There are no active organisations available for this playground.
              </p>
            )}
          </DialogContent>
        </Dialog>
        <div className="relative min-h-0 flex-1">
          <div
            ref={viewport}
            role="log"
            aria-label="Cash strategy conversation"
            className="h-full overflow-y-auto overscroll-contain px-4 py-8 sm:px-8"
            onScroll={(event) => {
              const element = event.currentTarget;
              follow.current =
                element.scrollHeight -
                  element.scrollTop -
                  element.clientHeight <
                100;
              setShowJump(!follow.current);
            }}
          >
            <div
              ref={threadContent}
              className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-8"
            >
              {turns.map((turn, index) => (
                <PlaygroundMessage
                  key={turn.id}
                  turn={turn}
                  currentInputs={planningInputs}
                  currentCashflowEdits={cashflowEdits}
                  selectionEnabled={
                    !busy &&
                    index === turns.length - 1 &&
                    (planningInputs.additionalContext === undefined ||
                      turn.editStep !== undefined)
                  }
                  onFundingConfirm={(funding) => {
                    let message =
                      "I confirm my current AUD and USD balances. Continue to product preferences.";
                    if (funding.topUps.length > 0) {
                      message =
                        "I confirm my current AUD and USD balances plus these planned top-ups. Continue to product preferences.";
                    }
                    if (planningInputs.additionalContext !== undefined) {
                      message =
                        "Use these confirmed balances and planned top-ups to regenerate the cash strategy.";
                    }
                    send(
                      message,
                      organisationId,
                      holdingEntityId,
                      cashflowConfirmation,
                      { ...planningInputs, funding },
                      cashflowEdits,
                      "FORM",
                    );
                  }}
                  onPreferencesConfirm={(preferences) =>
                    send(
                      planningInputs.additionalContext !== undefined
                        ? "Use these product preferences to regenerate the cash strategy."
                        : "Confirm these product preferences. Let me add any final planning details before generating the strategy.",
                      organisationId,
                      holdingEntityId,
                      cashflowConfirmation,
                      { ...planningInputs, preferences },
                      cashflowEdits,
                      "FORM",
                    )
                  }
                  onConstraintsConfirm={(additionalContext) =>
                    send(
                      additionalContext
                        ? `Generate the organisation’s cash strategy with this in mind: ${additionalContext}`
                        : "I have no additional planning details. Generate the organisation’s cash strategy.",
                      organisationId,
                      holdingEntityId,
                      cashflowConfirmation,
                      { ...planningInputs, additionalContext },
                      cashflowEdits,
                      "FORM",
                    )
                  }
                  onCashflowConfirm={(entityId, report, edits) => {
                    let message =
                      "I confirm the holding entity and three-month average cashflow. Continue to funding.";
                    if (edits) {
                      message =
                        "I confirm the holding entity and cashflow values I entered. Continue to funding.";
                    }
                    if (planningInputs.additionalContext !== undefined) {
                      message =
                        "Use this holding entity and cashflow to regenerate the cash strategy.";
                    }
                    send(
                      message,
                      organisationId,
                      entityId,
                      report.token,
                      planningInputs,
                      edits,
                      "FORM",
                    );
                  }}
                  onRenderError={reportRenderError}
                  onRetry={
                    !busy && pending.current?.assistantId === turn.id
                      ? () => {
                          if (pending.current) {
                            void generate({
                              ...pending.current,
                              attemptId: typeid(
                                "cash_mgt_strategy_attempt",
                              ).toString(),
                            });
                          }
                        }
                      : undefined
                  }
                />
              ))}
              {notice && (
                <p role="alert" className="text-danger text-sm">
                  {notice}
                </p>
              )}
            </div>
          </div>
          {showJump && (
            <div className="absolute inset-x-0 bottom-4 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ArrowDown />}
                onClick={() => {
                  follow.current = true;
                  setShowJump(false);
                  viewport.current?.scrollTo({
                    top: viewport.current.scrollHeight,
                    behavior: "smooth",
                  });
                }}
              >
                Jump to latest
              </Button>
            </div>
          )}
        </div>
        <footer className="shrink-0 border-t pt-8">
          <div className="mx-auto w-full max-w-7xl">
            {planningInputs.additionalContext !== undefined &&
              turns.some(
                (turn) =>
                  turn.role === "assistant" &&
                  turn.status === "complete" &&
                  turn.activities.length > 0,
              ) && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {[
                    "Explain the allocation",
                    "Show a simpler layout",
                    "Compare liquidity and yield",
                  ].map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => send(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              )}
            <form
              className="rounded-2xl border bg-canvas p-3 shadow-surface focus-within:border-brand-highlight"
              onSubmit={(event) => {
                event.preventDefault();
                send(draft);
              }}
            >
              <label htmlFor="cash-message" className="sr-only">
                Message the cash strategy assistant
              </label>
              <Textarea
                ref={composer}
                id="cash-message"
                rows={2}
                className="min-h-12 resize-none border-0 bg-transparent p-1 text-sm shadow-none focus-visible:ring-0"
                placeholder={
                  organisationId
                    ? "Ask a question or request a change to your inputs…"
                    : "Choose an organisation to begin"
                }
                value={draft}
                maxLength={8000}
                disabled={!organisationId || busy}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    !event.shiftKey &&
                    !event.nativeEvent.isComposing
                  ) {
                    event.preventDefault();
                    send(draft);
                  }
                }}
              />
              <div className="mt-1 flex items-center justify-between gap-3">
                <span className="text-secondary text-xs">
                  {busy
                    ? "Working on your reply…"
                    : "Enter to send · Shift + Enter for a new line"}
                </span>
                {busy ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    leftIcon={<Square />}
                    onClick={() => {
                      const assistantId = pending.current?.assistantId;
                      const request = pending.current;
                      if (request) {
                        void apolloClient
                          .mutate({
                            mutation: cancelCashManagementStrategyMutation,
                            variables: {
                              threadId: request.threadId,
                              attemptId: request.attemptId,
                            },
                          })
                          .catch((error: unknown) => {
                            setNotice(
                              error instanceof Error
                                ? error.message
                                : "Could not stop the reply.",
                            );
                          });
                      }
                      active.current?.abort();
                      active.current = null;
                      setBusy(false);
                      setTurns((previous) =>
                        previous.map((turn) =>
                          turn.id === assistantId
                            ? {
                                ...turn,
                                status: "cancelled",
                                error: "Reply stopped. Retry to continue.",
                                activities: turn.activities.map((step) =>
                                  step.status === "running"
                                    ? { ...step, status: "cancelled" }
                                    : step,
                                ),
                              }
                            : turn,
                        ),
                      );
                    }}
                  >
                    Stop
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="sm"
                    icon={<ArrowUp />}
                    aria-label="Send message"
                    disabled={!organisationId || !draft.trim()}
                  />
                )}
              </div>
            </form>
          </div>
        </footer>
      </main>
    </ThemeProvider>
  );
}
